import type { ModelTaskClass } from '@atbash-labs/constants'

export { ModelTaskClass } from '@atbash-labs/constants'

/**
 * Selector grouping from the proxy catalog. `primary` is the first screen
 * (Claude Sonnet 5.5, Haiku 5.5, Opus 5.5, and the current GPT-6 family).
 * `other` is the "Other models" submenu: every Bedrock model, GPT-5.6 and
 * older, and the previous Claude generation (Opus 5, Sonnet 5, Haiku 4.5).
 */
export type ModelTier = 'primary' | 'other'

/** One selectable provider/model pair, labeled for display. */
export interface ModelOption {
  provider: string
  model: string
  label: string
  /**
   * Thinking-depth values this model accepts, in increasing depth.
   * Empty when the model has no effort parameter.
   */
  effort_levels: ModelEffort[]
  /**
   * True when `effort_levels` is non-empty. Derived by the proxy for older
   * clients; prefer `effort_levels`.
   */
  supports_effort: boolean
  /** Absent on a proxy that predates tiering — read as `primary`. */
  tier?: ModelTier
  /** Provider-published context window in tokens (absent on older proxies). */
  context_window_tokens?: number
  /** Provider-published maximum reply tokens (absent on older proxies). */
  max_output_tokens?: number
}

/** A concrete provider/model pair without a display label. */
export interface ModelTarget {
  provider: string
  model: string
}

/**
 * Optional catalog-model → upstream deployment name. Azure AI Foundry hosts
 * often expose `gpt-5_5-gs-2026-04-24` for catalog `gpt-5.5`.
 */
export interface ModelDeployment {
  provider: string
  model: string
  deployment: string
}

export type ModelEffort =
  | 'default'
  | 'none'
  | 'minimal'
  | 'low'
  | 'medium'
  | 'high'
  | 'xhigh'
  | 'max'

/** Explicit effort levels (excludes policy "provider default"). */
export type ExplicitModelEffort = Exclude<ModelEffort, 'default'>

/** Shared labels for explicit effort levels; UIs prepend their own default entry. */
export const MODEL_EFFORT_LEVEL_OPTIONS: ReadonlyArray<{
  value: ExplicitModelEffort
  label: string
}> = [
  { value: 'none', label: 'None' },
  { value: 'minimal', label: 'Minimal' },
  { value: 'low', label: 'Low' },
  { value: 'medium', label: 'Medium' },
  { value: 'high', label: 'High' },
  { value: 'xhigh', label: 'Extra high' },
  { value: 'max', label: 'Maximum' }
]

export function catalogEffortLevels(option: ModelOption | undefined): ExplicitModelEffort[] {
  if (option === undefined) return []
  const listed = option.effort_levels ?? []
  const known = new Set(MODEL_EFFORT_LEVEL_OPTIONS.map((entry) => entry.value))
  return listed.filter((level): level is ExplicitModelEffort =>
    known.has(level as ExplicitModelEffort)
  )
}

export function effortLevelLabel(level: string): string {
  return MODEL_EFFORT_LEVEL_OPTIONS.find((option) => option.value === level)?.label ?? level
}

/** Provider posture returned by the proxy. Unavailable providers are not selectable. */
export interface ModelProvider {
  name: string
  label: string
  available: boolean
}

/** One ordered provider/model/effort attempt in a task-class routing chain. */
export interface ModelCandidate {
  provider: string
  model: string
  effort: ModelEffort
}

/** Ordered model fallback chain for one task class. */
export interface TaskClassRoutingPolicy {
  candidates: ModelCandidate[]
}

/** The default, effective, and resolved routing state for one task class. */
export interface TaskClassRoutingState {
  defaultPolicy: TaskClassRoutingPolicy
  effectivePolicy: TaskClassRoutingPolicy
  resolvedTarget: ModelTarget | null
  resolvedEffort: ModelEffort | null
}

/** Full replacement for one task class. `null` resets that class to its default. */
export interface TaskClassRoutingPolicyPatch {
  candidates: ModelCandidate[]
}

/**
 * The org's model policy as served by proxy `GET /api/v1/ontology/model-policy`.
 * `catalog` is filtered to configured providers. `allowedModels` is the
 * effective organization allowlist across the product catalog.
 * `modelDeployments` remaps catalog ids onto host-specific deployment names.
 */
export interface OrganizationModelPolicy {
  providers: ModelProvider[]
  catalog: ModelOption[]
  allowedModels: ModelTarget[]
  modelDeployments: ModelDeployment[]
  taskClassRouting: Record<ModelTaskClass, TaskClassRoutingState>
  fallback_enabled: boolean
  /** Absolute trigger override; null = window-relative / product default. */
  auto_compaction_trigger_input_tokens: number | null
  /**
   * Window-relative trigger (share of the serving model's context window,
   * 0.1–0.9); null = product default. An absolute trigger overrides it.
   */
  auto_compaction_trigger_fraction: number | null
  /** Product defaults the org inherits when both trigger fields are null. */
  compactionDefaults: CompactionDefaults
}

export interface CompactionDefaults {
  triggerInputTokens: number
  /** Window share the proxy compacts at when the org sets neither trigger (0.75). */
  triggerFraction: number
  /** Share of the window the prompt may fill before the reply budget (0.8). */
  contextBudgetFraction: number
  minTriggerInputTokens: number
  maxTriggerInputTokens: number
  minTriggerFraction: number
  maxTriggerFraction: number
}

/** PUT payload. Every field is independent so callers can avoid stale overwrites. */
export interface UpdateOrganizationModelPolicyPayload {
  taskClassRouting?: Partial<Record<ModelTaskClass, TaskClassRoutingPolicyPatch | null>>
  allowedModels?: ModelTarget[]
  modelDeployments?: ModelDeployment[]
  fallback_enabled?: boolean
  auto_compaction_trigger_input_tokens?: number | null
  auto_compaction_trigger_fraction?: number | null
}
